import { useState } from 'react';

import { useObservability } from 'shared/helpers/observability';
import { showToast } from '@opendoor/bricks/complex/Toast';

import { SetUpPurchaseContractFormContent } from 'sellDirect/components/closingDash/contract/SetUpPurchaseContractFormContent';

import {
  IResetableFormRenderProps,
  IResetableFormProps,
  ResetableForm,
} from 'sellDirect/components/closingDash/ResetableForm';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { FinalizedOffer } from 'sellDirect/OfferContext';
import { preContractDetailsFormToTasks } from 'sellDirect/helpers/closingDash/contracts';
import { useUpdateClosingTasks } from 'sellDirect/graphql/useUpdateClosingTasks';

interface PreContractDetailsFormProps {
  /** Finalized offer */
  offer: FinalizedOffer;
  /** Current closing tasks */
  closingTasks: ClosingTasks;
  /** Callback to call for setting the contract (And going to the next step in the flow) */
  goBackToSummary: () => void;
}

export interface PreContractDetailsFormState {
  ownerName?: string | null;
  hasSecondOwner?: boolean | null;
  secondOwnerName?: string | null;
  hasHoa?: boolean | null;
  hoaName?: string | null;
  hoaFees?: Array<{ amount?: number | null; frequency?: string | null }> | null;
  hoaAmenities?: {
    trashRecycling?: boolean | null;
    pool?: boolean | null;
    park?: boolean | null;
    dogPark?: boolean | null;
    gym?: boolean | null;
    sauna?: boolean | null;
    tennisCourt?: boolean | null;
    clubhouse?: boolean | null;
    golf?: boolean | null;
    trails?: boolean | null;
    sportsCourts?: boolean | null;
    other?: boolean | null;
    otherInfo?: string | null;
  } | null;
  closeDate?: string | null;
}

/**
 * Form that handles setting up the contract form
 *
 * storybook-check-ignore
 */
export const SetUpPurchaseContractForm: React.FC<PreContractDetailsFormProps> = ({
  offer,
  closingTasks,
  goBackToSummary,
}) => {
  const { trackEvent } = useObservability();
  const closeEscrowDate = closingTasks.setCloseDate?.taskable.selectedDate || offer.closeEscrowDate;
  const hoaFeesTask = closingTasks?.hoaFees?.taskable;
  const setSignatoriesTask = closingTasks?.setSignatories?.taskable;

  const [formState, setFormState] = useState<PreContractDetailsFormState>({
    hoaAmenities: {
      clubhouse: hoaFeesTask?.hoaAmenities?.clubhouse,
      dogPark: hoaFeesTask?.hoaAmenities?.dogPark,
      golf: hoaFeesTask?.hoaAmenities?.golf,
      gym: hoaFeesTask?.hoaAmenities?.gym,
      other: hoaFeesTask?.hoaAmenities?.other,
      otherInfo: hoaFeesTask?.hoaAmenities?.otherInfo,
      park: hoaFeesTask?.hoaAmenities?.park,
      pool: hoaFeesTask?.hoaAmenities?.pool,
      sauna: hoaFeesTask?.hoaAmenities?.sauna,
      sportsCourts: hoaFeesTask?.hoaAmenities?.sportsCourts,
      tennisCourt: hoaFeesTask?.hoaAmenities?.tennisCourt,
      trails: hoaFeesTask?.hoaAmenities?.trails,
      trashRecycling: hoaFeesTask?.hoaAmenities?.trashRecycling,
    },
    hasHoa: hoaFeesTask?.hasHoa,
    hoaName: hoaFeesTask?.hoaName,
    hoaFees: hoaFeesTask?.hoaFees?.map((item) => ({
      amount: item?.amount,
      frequency: item?.frequency,
    })),
    ownerName: setSignatoriesTask?.signerNames[0],
    hasSecondOwner: !!setSignatoriesTask?.signerNames[1],
    secondOwnerName: setSignatoriesTask?.signerNames[1],
    closeDate: closeEscrowDate?.toString(),
  });

  const { updateClosingTasks, loading } = useUpdateClosingTasks('update-pre-contract-details', {
    onCompleted: () => goBackToSummary(),
    errorPolicy: 'none',
    onError: () =>
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-update-pre-contract-details-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      ),
  });

  const submitPreContractDetails: IResetableFormProps['onSubmit'] = (state) => {
    const newFormState = state.formState as PreContractDetailsFormState;
    const preContractDetails = preContractDetailsFormToTasks(newFormState);
    setFormState(newFormState);
    trackEvent('click', 'save-contract-details');
    updateClosingTasks(preContractDetails);
  };

  return (
    <ResetableForm
      initialFormState={formState}
      onSubmit={submitPreContractDetails}
      render={(state: IResetableFormRenderProps) => (
        <SetUpPurchaseContractFormContent
          shouldSeeAgentExperience={offer.shouldSeeAgentExperience}
          closingTasks={closingTasks}
          loading={loading}
          resetableForm={state}
        />
      )}
    />
  );
};
